

import api from '../Api'

import {ICollectionResponse} from "../../models/ICollectionResponse";
import {IProductCategory, IProductCategoryMapping, IProductSegment} from "../../models/Storefront";
import {IActionResponse} from "../../dal/BaseDAL";
import {cloneDeep} from "lodash";
import {ILookupConditions} from "../../models/Extension";
import {getQueryStringFromConditions} from "../../logic/StringHelper";

const productCategorizationApi = api.injectEndpoints({
    endpoints: (build) => ({
        getAllProductCategories: build.query<ICollectionResponse<IProductCategory>, void>({
            query: () => `productCategorization/categories`
        }),
        getAllProductSegments: build.query<ICollectionResponse<IProductSegment>, void>({
            query: () => `productCategorization/segments`
        }),
        getCategoryMappingsByContext: build.query<ICollectionResponse<IProductCategoryMapping>, {context: string}>({
            query: (arg) => `productCategorization/categoryMappings/${arg.context}`
        }),
        saveCategoryMapping: build.mutation<IActionResponse, {mapping: IProductCategoryMapping, context: string}>({
            query: (arg) => ({
                url: `productCategorization/categoryMappings/${arg.context}`,
                method: 'POST',
                body: arg.mapping
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                try {
                    const result = await queryFulfilled;

                    dispatch(
                        productCategorizationApi.util.updateQueryData('getCategoryMappingsByContext', {context: arg.context}, draft => {
                            const copy = cloneDeep(draft)
                            const index = draft.items.findIndex(a => a.id && a.id === result.data.id)

                            if (index !== -1) {
                                const mapping = {...draft.items[index], ...arg.mapping, id: result.data.id}
                                copy.items.splice(index, 1, mapping);
                            }
                            else {
                                const mapping = {...arg.mapping, id: result.data.id}
                                copy.items.push(mapping);
                            }
                            Object.assign(draft, copy);
                        })
                    );


                } catch (exception) {
                    console.log(exception)
                }

            }
        }),
        deleteCategoryMapping: build.mutation<IActionResponse, {id: string, context: string}>({
            query: (arg) => ({
                url: `productCategorization/categoryMappings/${arg.id}`,
                method: 'DELETE',
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                try {
                    const result = await queryFulfilled;

                    dispatch(
                        productCategorizationApi.util.updateQueryData('getCategoryMappingsByContext', {context: arg.context}, draft => {
                            const copy = cloneDeep(draft)
                            const index = draft.items.findIndex(a => a.id && a.id === result.data.id)

                            if (index !== -1) {
                                copy.items.splice(index, 1);
                            }
                            Object.assign(draft, copy);
                        })
                    );
                } catch (exception) {
                    console.log(exception)
                }

            }
        }),
        updateCategoryMapping: build.mutation<IActionResponse, {mapping: IProductCategoryMapping, id: string, context: string}>({
            query: (arg) => ({
                url: `productCategorization/categoryMappings/${arg.context}/${arg.id}`,
                method: 'PUT',
                body: arg.mapping
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                try {
                    const result = await queryFulfilled;
                    dispatch(
                        productCategorizationApi.util.updateQueryData('getCategoryMappingsByContext', {context: arg.context}, draft => {
                            const copy = cloneDeep(draft)
                            const index = draft.items.findIndex(a => a.id && a.id === result.data.id)
                            if (index !== -1) {
                                const mapping = {...arg.mapping, id: result.data.id}
                                copy.items.splice(index, 1, mapping);
                                Object.assign(draft, copy);
                            }
                        })
                    );
                } catch (exception) {
                    console.log(exception)
                }
            }
        }),
        getManufacturers: build.query<ICollectionResponse<string>, void>({
            query: () => `productCategorization/manufacturers`
        }),
        getSubCategories: build.query<ICollectionResponse<string>, { categories: string[] }>({
            query: (arg) => {
                const params = arg.categories.map(c => `categories=${encodeURIComponent(c)}`).join('&');
                return `productCategorization/subcategories${params ? `?${params}` : ''}`;
            }
        }),
        getProductCategoriesBySegment: build.query<ICollectionResponse<IProductCategory>, string>({
            query: (arg) => `productCategorization/segments/${arg}/categories`
        }),
        // Per-argument-cached keyword searches for the global search palette. The merged list
        // queries below collapse every arg onto one cache entry for the management grids, so the
        // palette must not share them (same rationale as getContacts in Api.ts).
        // searchBy is required: the server only applies searchTerm when a target field accompanies
        // it (Cosmos ApplyConditions no-ops on a bare searchTerm and returns page 1 of everything).
        searchProductCategoryRefs: build.query<ICollectionResponse<IProductCategory>, { searchTerm: string, pageSize?: number }>({
            query: (arg) => `productCategorization/categories/merged` + getQueryStringFromConditions({page: 1, pageSize: arg.pageSize ?? 5, searchTerm: arg.searchTerm, searchBy: 'label'}),
        }),
        searchProductSegmentRefs: build.query<ICollectionResponse<IProductSegment>, { searchTerm: string, pageSize?: number }>({
            query: (arg) => `productCategorization/segments/merged` + getQueryStringFromConditions({page: 1, pageSize: arg.pageSize ?? 5, searchTerm: arg.searchTerm, searchBy: 'label'}),
        }),
        getMergedProductCategories: build.query<ICollectionResponse<IProductCategory>, { conditions?: ILookupConditions } >({
            query: (arg) => `productCategorization/categories/merged` + getQueryStringFromConditions(arg.conditions),
            serializeQueryArgs: ({endpointName}) => {
                return endpointName
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getCategoryById: build.query<IProductCategory, { categoryIdentifier: string, segmentIdentifier: string }>({
            query: (arg) => `productCategorization/segments/${arg.segmentIdentifier}/categories/${arg.categoryIdentifier}`
        }),
        saveCustomStorefrontCategory: build.mutation<IActionResponse, {category: IProductCategory}>({
            query: (arg) => ({
                url: `productCategorization/categories`,
                method: 'PUT',
                body: arg.category
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                try {
                    const result = await queryFulfilled;

                    const patchResult = dispatch(
                        productCategorizationApi.util.updateQueryData('getMergedProductCategories', {}, draft => {
                            const copy = cloneDeep(draft)
                            const index = draft.items.findIndex(a => a.id && a.id === result.data.id)
                            if (index !== -1) {
                                copy.items.splice(index, 1, arg.category);
                                Object.assign(draft, copy);
                            }
    
                        })
                    );
    

                } catch (exception) {
                    console.log(exception)
                }

            }
        }),
        getSegmentById: build.query<IProductSegment, { segmentIdentifier: string}>({
            query: (arg) => `productCategorization/segments/${arg.segmentIdentifier}`
        }),
        getMergedProductSegments: build.query<ICollectionResponse<IProductSegment>, { conditions?: ILookupConditions } >({
            query: (arg) => `productCategorization/segments/merged` + getQueryStringFromConditions(arg.conditions),
            serializeQueryArgs: ({endpointName}) => {
                return endpointName
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        saveCustomStorefrontSegment: build.mutation<IActionResponse, {segment: IProductSegment}>({
            query: (arg) => ({
                url: `productCategorization/segments`,
                method: 'PUT',
                body: arg.segment
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                try {
                    const result = await queryFulfilled;
                    if (!result.data.success) {
                        return;
                    }
    
                    const patchResult = dispatch(
                        productCategorizationApi.util.updateQueryData('getMergedProductSegments', {}, draft => {
                            const copy = cloneDeep(draft)
                            const index = draft.items.findIndex(a => a.id && a.id === result.data.id)
                            if (index !== -1) {
                                copy.items.splice(index, 1, arg.segment);
                                Object.assign(draft, copy);
                            }
    
                        })
                    );

                } catch (exception) {
                    console.log('exception fulfilling query', exception)
                }

            }
        })
    }),
    overrideExisting: false,
})

export const {
    useGetCategoryByIdQuery,
    useSearchProductCategoryRefsQuery,
    useSearchProductSegmentRefsQuery,
    useGetAllProductCategoriesQuery,
    useGetAllProductSegmentsQuery,
    useGetManufacturersQuery,
    useGetSubCategoriesQuery,
    useLazyGetProductCategoriesBySegmentQuery,
    useGetProductCategoriesBySegmentQuery,
    useGetMergedProductCategoriesQuery,
    useSaveCustomStorefrontCategoryMutation,
    useGetSegmentByIdQuery,
    useGetMergedProductSegmentsQuery,
    useSaveCustomStorefrontSegmentMutation,
    useGetCategoryMappingsByContextQuery,
    useSaveCategoryMappingMutation,
    useDeleteCategoryMappingMutation,
    useUpdateCategoryMappingMutation
} = productCategorizationApi
